import { HealthAndSafetyRounded, LaunchRounded } from "@mui/icons-material";
import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { controlKeys, remediationKeys } from "../../../../../api/queryKeys";
import CustomToolTip from "../../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { DRAWER_TABS, RemediationType } from "../../../../../utils/constants";
import ChipColor from "../../../../remediation/plan/components/ChipColor";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";
import ControlRemediationDrawer from "../../../controls/components/ControlRemediationDrawer/ControlRemediationDrawer";
const TopImpactRemediations = ({ filteredScenariosSets }) => {
  const colors = useColors();
  const location = useLocation();
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [selectedControl, setSelectedControl] = useState(null);
  const [remediationIdClicked, setRemediationIdClicked] = useState(null);
  const [topImpactRemediations, setTopImpactRemediations] = useState([]);
  const { fetchAllControls, trackEvent, fetchAllRemediations } = useAPI();
  const onDrawerClose = () => {
    setSelectedControl(null);
    setOpenDrawer(false);
    setSearchParams({});
  };
  const { data: controls, isLoading: loadingControls } = useQuery({
    queryKey: controlKeys.all,
    queryFn: fetchAllControls,
    select: (data) =>
      data.data.map((control) => ({
        ...control,
        id: control.control_id,
      })),
  });
  const { data: remediations, isLoading: isLoadingRemediation } = useQuery({
    queryKey: remediationKeys.all,
    queryFn: fetchAllRemediations,
    select: (response) =>
      response?.data.map((remediation) => ({
        id: remediation.remediation_id,
        ...remediation,
      })),
  });
  const aggregateControlValues = (scenarioSets) => {
    const aggregatedControls = {};

    scenarioSets?.forEach((scenarioSet) => {
      Object.entries(scenarioSet?.remediations_info).forEach(
        ([controlId, value]) => {
          if (!aggregatedControls[controlId]) {
            aggregatedControls[controlId] = 0;
          }
          aggregatedControls[controlId] += value;
        }
      );
    });

    return aggregatedControls;
  };

  useEffect(() => {
    if (
      filteredScenariosSets?.length > 0 &&
      remediations?.length > 0 &&
      controls?.length > 0
    ) {
      const aggregatedControls = aggregateControlValues(filteredScenariosSets);
      const topRemediations = Object.entries(aggregatedControls)
        .map(([controlId, value]) => {
          const remediation = remediations.find((remediation) => {
            return remediation.control_id.toString() === controlId;
          });

          // Find the associated control to check if it's snoozed
          const control = controls.find(
            (control) => control.control_id.toString() === controlId
          );

          return {
            remediation,
            value,
            isSnoozed: control?.is_snoozed || false,
          };
        })
        // Filter out snoozed controls
        .filter((item) => !item.isSnoozed)
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);
      setTopImpactRemediations(topRemediations);
    } else {
      setTopImpactRemediations([]);
    }
  }, [filteredScenariosSets, remediations, controls]);

  const handleViewAllClick = () => {
    navigate("/remediation/plan");
    trackEvent("Top Impact Remediations View All Clicked");
  };

  useEffect(() => {
    const localSearchParams = new URLSearchParams(location.search);

    const remediationId = localSearchParams.get("remediation_id");
    if (remediationId) {
      setRemediationIdClicked(remediationId);
      const selectedControl = controls?.find(
        (control) => control.control_id.toString() === remediationId.toString()
      );
      setSelectedControl(selectedControl);
      setOpenDrawer(true);
    }
  }, [location, controls]);

  const handleRowClick = async (column) => {
    const remediationId = column.row.id;
    setRemediationIdClicked(remediationId);
    const selectedControl = controls.find(
      (control) => control.control_id.toString() === remediationId.toString()
    );
    setSelectedControl(selectedControl);
    setOpenDrawer(true);
    setSearchParams({ remediation_id: remediationId });

    trackEvent(
      `Click on remediation row with remediation id: ${column?.row?.remediation_id}`
    );

    // if (isPOC) {
    //   handlePOCClick(remediationId);
    // } else {
    //   handleNonPOCClick(column, selectedControl);
    // }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      renderHeader: (params) => {
        return (
          <Typography variant="overline">
            {params.colDef?.headerName}
          </Typography>
        );
      },
      width: 0.05,
      renderCell: (params) => {
        return <Typography variant="caption">#{params.value}</Typography>;
      },
    },
    {
      headerName: "Remediation Name",
      renderHeader: (params) => {
        return (
          <Typography variant="overline">
            {params.colDef?.headerName}
          </Typography>
        );
      },
      field: "name",
      headerAlign: "left",
      sortable: false,
      flex: 0.55,
      renderCell: (params) => {
        return (
          <CustomToolTip title={params.value?.length > 50 ? params.value : ""}>
            <Typography variant="caption">{params.value}</Typography>
          </CustomToolTip>
        );
      },
    },
    {
      headerName: "Tool",
      field: "tool",
      headerAlign: "start",
      renderHeader: (params) => {
        return (
          <Typography variant="overline" sx={{ marginLeft: "5px" }}>
            {params.colDef?.headerName}
          </Typography>
        );
      },
      sortable: false,
      flex: 0.05,
      renderCell: (params) => (
        <SvgToIcon
          IconLogo={IntegrationsMapping[params.value.toLowerCase()]?.logo}
          width="40"
        />
      ),
    },
    {
      headerName: "Feasibility",
      field: "feasibility",
      renderHeader: (params) => {
        return (
          <Typography variant="overline">
            {params.colDef?.headerName}
          </Typography>
        );
      },
      align: "left",
      headerAlign: "left",
      sortable: false,
      flex: 0.2,
      renderCell: (params) => {
        return (
          <ChipColor value={params.value} type={RemediationType.FEASIBILITY} />
        );
      },
    },

    {
      headerName: "Impact",
      headerAlign: "left",
      renderHeader: (params) => {
        return (
          <Typography variant="overline">
            {params.colDef?.headerName}
          </Typography>
        );
      },
      flex: 0.15,
      align: "left",
      sortable: false,
      renderCell: (params) => {
        // Calculate total scenario count across all filtered scenario sets
        const totalScenarioCount = filteredScenariosSets
          ?.map((scenarioSet) => scenarioSet.scenario_count)
          .reduce((a, b) => a + b, 0);

        // Calculate the percentage but cap it at 100%
        let coverageImprovement = Math.min(
          Math.round((params.row.scenariosCount / totalScenarioCount) * 100),
          100
        );

        // Ensure we show at least 1% for very small impacts
        coverageImprovement =
          coverageImprovement === 0 ? 1 : coverageImprovement;

        return (
          <Box display={"flex"} gap={1} alignItems={"center"}>
            {filteredScenariosSets?.length > 0 && (
              <Box display={"flex"} gap={1} alignItems={"center"}>
                <Typography variant="caption">
                  {coverageImprovement}%
                </Typography>
                <NorthRoundedIcon
                  sx={{
                    color: colors.success_400,
                    width: "16px",
                    height: "16px",
                  }}
                />
              </Box>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        borderRadius: "6px",
        boxShadow: "0px 0px 2px 1px " + colors.divider2,
        backgroundColor: colors.common_white,
        padding: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        display={"flex"}
        gap={2}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Box display={"flex"} gap={1} alignItems={"center"}>
          <HealthAndSafetyRounded
            width={"16px"}
            sx={{ color: colors.primary_main }}
          />
          <Typography variant="subtitle1">
            {"Top Impactful Remediations"}
          </Typography>
        </Box>
        <Box
          display={"flex"}
          gap={1}
          alignItems={"center"}
          sx={{ cursor: "pointer" }}
          onClick={handleViewAllClick}
        >
          <Typography variant="caption">{"View All"}</Typography>
          <LaunchRounded sx={{ width: "18px" }} />
        </Box>
      </Box>
      <StyledDataGrid
        style={{ cursor: "pointer" }}
        columns={columns}
        rows={topImpactRemediations.map((remediation) => ({
          id: remediation.remediation.control_id,
          name: remediation.remediation.name,
          feasibility: remediation.remediation.feasibility,
          tool: remediation.remediation.tool,
          scenariosCount: remediation.value,
        }))}
        pageSize={5}
        rowHeight={40}
        columnHeaderHeight={30}
        onRowClick={handleRowClick}
        hideFooter
        loading={loadingControls || isLoadingRemediation}
        boxSXProps={{
          "& .MuiDataGrid-root": {
            border: "none",
            boxShadow: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeader": {
            cursor: "default",
          },
          border: "none ",
        }}
      />
      <ControlRemediationDrawer
        open={openDrawer}
        onClose={() => {
          onDrawerClose();
        }}
        selectedTab={DRAWER_TABS.REMEDIATION_TAB}
        selectedControl={selectedControl}
      />
    </Box>
  );
};

export default TopImpactRemediations;
